import React, { useEffect, useRef, useState } from 'react'
import { useAccess } from '../../../access/AccessContext'
import { useSessionOperationReady } from '../../../lib/SessionOperationContext'
import { readCreation, readReassessments, sendCreation } from '../services/reassessmentCreationApi'

function Creation({ program, actor, guard, disabled, payload, onOpen, onDenied, showCreate = true }) {
    const key = `rigo:reassessment-creation:${program}:${actor}`
    const [state, setState] = useState('LOADING'), [message, setMessage] = useState('')
    const mounted = useRef(false), locked = useRef(true), intent = useRef(null)
    const current = item => mounted.current && intent.current === item && guard.isCurrent?.() === true
    const unresolved = () => { setState('UNRESOLVED'); setMessage('Creation status could not yet be verified. Do not submit again.') }
    const confirm = (result, item) => {
        if (!current(item)) return false
        const row = result?.reassessment
        if (result?.operation_id !== item.id || result.status !== 'SUCCESS' || !row?.reassessment_reference || !['DRAFT', 'COMPLETED'].includes(row.status)) return false
        // Storage cleanup failure cannot reopen the mutation control.
        try { sessionStorage.removeItem(key) } catch { /* Keep a harmless reconcile-only marker. */ }
        setState('SUCCESS')
        if (row.status === 'DRAFT') { setMessage('Reassessment created successfully.'); onOpen(row) }
        else setMessage(`Creation of reassessment #${row.sequence} is confirmed. It is already completed; review it in reassessment history.`)
        return true
    }
    const reconcile = async item => {
        if (!current(item)) return
        setState('RECONCILING'); setMessage('Verifying reassessment creation status…')
        try { if (confirm(await readCreation(program, item.id), item)) return } catch { /* UNKNOWN is not proof of noncreation. */ }
        if (current(item)) unresolved()
    }
    useEffect(() => {
        mounted.current = true
        try {
            const value = sessionStorage.getItem(key)
            if (value) {
                const marker = JSON.parse(value)
                if (marker.program !== program || marker.actor !== actor || typeof marker.id !== 'string') throw new Error()
                const item = { id: marker.id, sent: true }; intent.current = item
                reconcile(item)
            } else { locked.current = false; setState('READY') }
        } catch { setState('BLOCKED'); setMessage('Browser recovery storage is unavailable. Creation is disabled.') }
        return () => { mounted.current = false; intent.current = null }
        // Scope changes remount this component; recovery never replays POST.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
    const create = async () => {
        if (locked.current || disabled || !actor || !showCreate) return
        locked.current = true
        const item = { id: null, sent: false, payload: { ...payload } }; intent.current = item
        const ready = async () => {
            if (!current(item)) return false
            if (!guard() && !await guard.ensureReady?.()) return false
            return current(item) && guard()
        }
        ready.isCurrent = () => current(item) && guard()
        setState('PREPARING'); setMessage('Preparing reassessment creation; waiting for session readiness if necessary…')
        try {
            item.id = crypto.randomUUID()
            if (!await ready()) throw Object.assign(new Error('Creation was not sent. Session changed.'), { notSent: true })
            const rows = await readReassessments(program)
            if (!current(item)) return
            if (!Array.isArray(rows)) throw Object.assign(new Error('Creation was not sent. History could not be verified.'), { notSent: true })
            const existing = rows.find(row => row.status === 'DRAFT')
            if (existing) { setState('EXISTING'); setMessage('An existing draft was found; this activation created nothing.'); onOpen(existing); return }
            const result = await sendCreation(program, item.id, item.payload, { ready, onSending: () => {
                sessionStorage.setItem(key, JSON.stringify({ id: item.id, program, actor }))
                item.sent = true; setState('SENDING'); setMessage('Creating reassessment… Do not repeat the action.')
            } })
            if (!current(item)) return
            if (!confirm(result, item)) await reconcile(item)
        } catch (failure) {
            if (!current(item)) return
            if (!item.sent) { locked.current = false; setState('READY'); setMessage(failure.message || 'Creation was not sent.') }
            else {
                await reconcile(item)
                if (current(item) && failure.response?.status === 403) onDenied?.()
            }
        }
    }
    if (!showCreate && ['LOADING', 'READY'].includes(state)) return null
    return <div aria-busy={['LOADING', 'PREPARING', 'SENDING', 'RECONCILING'].includes(state)}>
        {showCreate && <button type="button" onClick={create} disabled={disabled || state !== 'READY'} className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white">Create New Reassessment</button>}
        {message && <p role="status" aria-live="polite">{message}</p>}
        {state === 'UNRESOLVED' && <button type="button" onClick={() => reconcile(intent.current)}>Check creation status</button>}
    </div>
}

export default function ReassessmentCreation(props) {
    const access = useAccess(), guard = useSessionOperationReady()
    return <Creation key={`${props.program}:${access.username}:${guard.generation}`} {...props} actor={access.username} guard={guard} />
}
